import React, { useEffect, useState } from 'react'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/react-hooks'
import client from 'apollo'
import confetti from 'canvas-confetti'
import { CHECK_BIRTHDAY_POPUP_STATUS } from 'modules/Messages/graphql/Queries'
import { MARK_BIRTHDAY_POPUP_VISITED } from 'modules/Messages/graphql/Mutations'

const BirthdayModal = ({ isClient }) => {
  const [visible, setVisible] = useState(false)
  const [modalData, setModalData] = useState(null)

  // The popup can change state at most once a day (birthday rollover, admin
  // activating the message, or an admin-triggered manual send resetting
  // popupVisited). Polling only needs to be frequent enough to catch those
  // without a page refresh - 2 minutes is ample, and keeps this off the
  // server's back on the other 364 days when it always returns showModal: false.
  const { data } = useQuery(CHECK_BIRTHDAY_POPUP_STATUS, {
    fetchPolicy: 'network-only',
    pollInterval: 120000,
    skip: !isClient,
  })

  useEffect(() => {
    if (data?.checkBirthdayPopupStatus?.showModal) {
      setModalData(data.checkBirthdayPopupStatus)
      setVisible(true)
    } else {
      setVisible(false)
    }
  }, [data])

  useEffect(() => {
    if (visible) {
      // 1. Initial realistic central explosion burst
      const count = 200
      const defaults = {
        origin: { y: 0.7 },
        zIndex: 10000,
      }

      const fire = (particleRatio, opts) => {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        })
      }

      fire(0.25, {
        spread: 30,
        startVelocity: 60,
        colors: ['#FFD700', '#FF1744', '#00E676', '#651FFF', '#FF4081'],
      })
      fire(0.2, {
        spread: 60,
        colors: ['#FFD700', '#D4AF37', '#FFF8DC'],
      })
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      })
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      })
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      })

      // 2. Realistic Left and Right Cannon blasts (3 seconds celebratory sequence)
      const duration = 3.5 * 1000
      const animationEnd = Date.now() + duration

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now()
        if (timeLeft <= 0) {
          return clearInterval(interval)
        }
        const particleCount = 45 * (timeLeft / duration)

        // Left side cannon
        confetti({
          particleCount,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.75 },
          zIndex: 10000,
          colors: ['#FFD700', '#E63946', '#F4A261', '#2A9D8F', '#E76F51', '#9D4EDD'],
        })

        // Right side cannon
        confetti({
          particleCount,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.75 },
          zIndex: 10000,
          colors: ['#FFD700', '#E63946', '#F4A261', '#2A9D8F', '#E76F51', '#9D4EDD'],
        })
      }, 250)

      return () => clearInterval(interval)
    }
  }, [visible])

  const handleClose = () => {
    client
      .mutate({
        mutation: MARK_BIRTHDAY_POPUP_VISITED,
      })
      .catch((err) => console.log('Error marking birthday popup visited:', err))
    setVisible(false)
  }

  if (!visible || !modalData) return null

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={520}
      bodyStyle={{
        padding: 0,
        borderRadius: '12px',
        overflow: 'visible',
        textAlign: 'center',
        background: '#fff',
      }}
    >
      <div style={{ position: 'relative', width: '100%', padding: '20px' }}>
        <button
          onClick={handleClose}
          aria-label='Close'
          style={{
            position: 'absolute',
            top: '-12px',
            right: '-12px',
            zIndex: 10,
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#1f1f1f',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            cursor: 'pointer',
            border: '2px solid #ffffff',
            outline: 'none',
          }}
        >
          <CloseOutlined style={{ fontSize: '14px', color: '#ffffff' }} />
        </button>

        {modalData.imageUrl ? (
          <img
            src={modalData.imageUrl}
            alt='Happy Birthday'
            style={{
              width: '100%',
              maxHeight: '480px',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        ) : (
          <div style={{ padding: '20px 10px' }}>
            <h2 style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '10px' }}>
              <span role='img' aria-label='party popper'>🎉</span> {modalData.title || 'Happy Birthday!'} <span role='img' aria-label='party popper'>🎉</span>
            </h2>
            <p style={{ fontSize: '16px', color: '#444' }}>
              {modalData.description ||
                'Wishing you a day filled with happiness, good health, and memorable moments.'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default BirthdayModal
